import React from "react";
import ReactDOM from "react-dom/client";
import * as AnimationModule from "./animations";
import * as ComponentsModule from "./components";

// Global props registry for efficient data storage
window.ReactPropsRegistry = window.ReactPropsRegistry || new Map();

// Cache for React roots to avoid recreating them
const rootCache = new WeakMap();

// Priority queue for component rendering
class RenderQueue {
  constructor() {
    this.critical = [];
    this.normal = [];
    this.deferred = [];
    this.processing = false;
  }

  add(node, priority = 'normal') {
    const item = { node, priority, timestamp: performance.now() };

    switch (priority) {
      case 'critical':
        this.critical.push(item);
        break;
      case 'deferred':
        this.deferred.push(item);
        break;
      default:
        this.normal.push(item);
    }

    if (!this.processing) {
      this.process();
    }
  }

  process() {
    this.processing = true;

    const processQueue = (queue, batchSize = 2) => {
      if (queue.length === 0) return false;

      const batch = queue.splice(0, batchSize);
      batch.forEach(({ node }) => {
        if (node.isConnected) { // Ensure node is still in DOM
          this.renderCallback?.(node);
        }
      });

      return queue.length > 0;
    };

    const scheduleNext = () => {
      if (window.scheduler?.postTask) {
        scheduler.postTask(() => this.processNext(), { priority: 'user-blocking' });
      } else if (window.requestIdleCallback) {
        requestIdleCallback(() => this.processNext(), { timeout: 8 });
      } else {
        requestAnimationFrame(() => this.processNext());
      }
    };

    this.processNext = () => {
      // Process critical first, then normal, then deferred
      if (processQueue(this.critical, 1)) { // Smaller batches for critical
        scheduleNext();
      } else if (processQueue(this.normal, 2)) {
        scheduleNext();
      } else if (processQueue(this.deferred, 3)) { // Larger batches for deferred
        scheduleNext();
      } else {
        this.processing = false;
      }
    };

    this.processNext();
  }
}

const renderQueue = new RenderQueue();

// Enhanced performance monitoring with frame timing
const createPerformanceLogger = () => ({
  logs: [],
  startTime: performance.now(),

  logStart: function (component, nodeIndex) {
    const start = performance.now();
    this.logs.push({
      component,
      nodeIndex,
      start,
      props: null,
      render: null,
      transition: null,
      end: null,
      total: null,
    });
    return this.logs.length - 1;
  },
  logProps: function (index, time) {
    if (this.logs[index]) {
      this.logs[index].props = time - this.logs[index].start;
    }
  },
  logRender: function (index, time) {
    if (this.logs[index]) {
      this.logs[index].render = time - this.logs[index].start;
    }
  },
  logTransition: function (index, time) {
    if (this.logs[index]) {
      this.logs[index].transition = time - this.logs[index].start;
    }
  },
  logEnd: function (index, time) {
    if (this.logs[index]) {
      this.logs[index].end = time;
      this.logs[index].total = time - this.logs[index].start;
    }
  },
  printSummary: function () {
    if (this.logs.length > 0 && window.performance?.mark) {
      performance.mark('react-rendering-complete');

      const totalTime = performance.now() - this.startTime;
      const slowComponents = this.logs
        .filter(log => log.total > 16) // Components taking more than one frame
        .sort((a, b) => (b.total || 0) - (a.total || 0));

      if (slowComponents.length > 0) {
        console.warn('🐌 Slow rendering components:', slowComponents);
      }

      // Report overall rendering performance
      if (totalTime > 100) {
        console.warn(`⚠️ Total rendering time: ${totalTime.toFixed(2)}ms for ${this.logs.length} components`);
      }
    }
  },
});

// Utility to detect Shopify theme editor
function isShopifyEditor() {
  return window.Shopify && window.Shopify.designMode;
}

export function addListeners() {
  // Mark performance timing
  if (window.performance?.mark) {
    performance.mark('react-renderer-init');
  }

  // Initialize registry from script tags first
  initializePropsRegistry();

  // Optimize DOM ready handling
  const handleDOMReady = async () => {
    AnimationModule.start();
    await renderReactBlocks(ComponentsModule.getComponents());
  };

  if (document.readyState === 'complete') {
    // Already loaded, render immediately
    handleDOMReady();
  } else if (document.readyState === 'interactive') {
    // DOM ready but resources loading
    requestAnimationFrame(handleDOMReady);
  } else {
    // Still loading
    document.addEventListener("DOMContentLoaded", handleDOMReady, { once: true, passive: true });
  }

  // Optimize viewport-based rendering
  const setupIntersectionObserver = () => {
    if (!window.IntersectionObserver) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !entry.target.getAttribute('injected')) {
            renderQueue.add(entry.target, 'normal');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '100px', // Start loading before visible
        threshold: 0.01
      }
    );

    // Observe all unrendered components
    document.querySelectorAll('[data-theme-id]:not([injected])').forEach(node => {
      // Check if component is above the fold for priority
      const rect = node.getBoundingClientRect();
      const isAboveFold = rect.top < window.innerHeight + 200;

      if (isAboveFold) {
        renderQueue.add(node, 'critical');
      } else {
        observer.observe(node);
      }
    });
  };

  // Use scheduler-friendly approach with intersection observer
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      if (document.readyState === "loading") {
        setupMutationObserver();
      } else {
        setupIntersectionObserver();
      }
    }, { timeout: 50 });
  } else {
    requestAnimationFrame(() => {
      if (document.readyState === "loading") {
        setupMutationObserver();
      } else {
        setupIntersectionObserver();
      }
    });
  }

  // Optimize cart updates with debouncing
  let cartUpdateTimeout;
  document.addEventListener("liquid-ajax-cart:request-end", (event) => {
    if (cartUpdateTimeout) {
      clearTimeout(cartUpdateTimeout);
    }

    cartUpdateTimeout = setTimeout(async () => {
      initializePropsRegistry();
      await renderReactBlocks(ComponentsModule.getComponents());
    }, 50); // Reduced delay for faster updates
  });

  // Always refresh registry and re-render in Shopify editor on block change
  if (isShopifyEditor()) {
    // Only re-render the affected section/block, not all blocks
    document.addEventListener('shopify:section:load', async (e) => {
      initializePropsRegistry(true);
      const section = e.target || e.detail?.sectionId && document.getElementById(e.detail.sectionId);
      if (section) {
        const nodes = Array.from(section.querySelectorAll('[data-theme-id]'));
        await renderReactBlocks(ComponentsModule.getComponents(), true, nodes);
      }
    });
    document.addEventListener('shopify:block:select', (e) => {
      // initializePropsRegistry(true);
      // const block = e.target || e.detail?.blockId && document.getElementById(e.detail.blockId);
      // if (block) {
      //   const nodes = Array.from(block.querySelectorAll('[data-theme-id]'));
      //   renderReactBlocks(ComponentsModule.getComponents(), true, nodes);
      // }
    });
    document.addEventListener('shopify:block:deselect', (e) => {
      // initializePropsRegistry(true);
      // const block = e.target || e.detail?.blockId && document.getElementById(e.detail.blockId);
      // if (block) {
      //   const nodes = Array.from(block.querySelectorAll('[data-theme-id]'));
      //   renderReactBlocks(ComponentsModule.getComponents(), true, nodes);
      // }
    });
    document.addEventListener('shopify:section:deselect', (e) => {
      // initializePropsRegistry(true);
      // const section = e.target || e.detail?.sectionId && document.getElementById(e.detail.sectionId);
      // if (section) {
      //   const nodes = Array.from(section.querySelectorAll('[data-theme-id]'));
      //   renderReactBlocks(ComponentsModule.getComponents(), true, nodes);
      // }
    });
  }
}

// Initialize props registry from script tags with batching
function initializePropsRegistry(force = false) {
  const registryScripts = document.querySelectorAll('script[data-props-registry-id]');
  if (registryScripts.length === 0) return;

  // Merge new props into registry, never overwrite or remove existing entries
  registryScripts.forEach(script => {
    try {
      const registryData = JSON.parse(script.textContent);
      const registryId = registryData.registryId;
      if (registryId && registryData.data) {
        // Merge props if already present, otherwise set
        const existing = window.ReactPropsRegistry.get(registryId) || {};
        window.ReactPropsRegistry.set(registryId, { ...existing, ...registryData.data });
      }
    } catch (error) {
      console.error('Error parsing props registry script:', error, script.textContent);
    }
  });
}

// Optimized mutation observer with debouncing
function setupMutationObserver() {
  let debounceTimer;

  const observer = new MutationObserver((mutationsList) => {
    clearTimeout(debounceTimer);

    debounceTimer = setTimeout(async () => {
      let hasNewComponents = false;

      for (const mutation of mutationsList) {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const registryScripts = node.querySelectorAll ? node.querySelectorAll('script[data-props-registry-id]') : [];
              if (registryScripts.length > 0 || node.matches?.('script[data-props-registry-id]')) {
                hasNewComponents = true;
                break;
              }
            }
          }
        }
        if (hasNewComponents) break;
      }

      if (hasNewComponents) {
        initializePropsRegistry();
        const components = await ComponentsModule.getComponents();
        if (components && components.length) {
          await renderReactBlocks(components);
        }
      }
    }, 100); // Debounce mutations
  });

  observer.observe(document.body, { childList: true, subtree: true });
  setTimeout(() => observer.disconnect(), 10000);
}

// Optimized dimension preservation
function preserveDimensions(element) {
  // Remove placeholder logic; return a no-op cleanup
  return () => {};
}

export async function renderReactBlocks(component_list, force = false, targetNodes = null) {
  const renderedNodes = new WeakSet();
  const perfLog = createPerformanceLogger();

  try {
    // Handle both sync and async component lists
    const resolvedComponents = Array.isArray(component_list)
      ? component_list
      : await component_list;

    const allNodes = targetNodes || Array.from(document.querySelectorAll("[data-theme-id]"));
    const componentMap = new Map(resolvedComponents.map(comp => [comp.name, comp]));

    // Always remove 'injected' attribute in Shopify editor or when force is true
    if (force || isShopifyEditor()) {
      allNodes.forEach(node => {
        if (node.getAttribute("injected")) {
          node.removeAttribute("injected");
        }
      });
    }

    // More accurate viewport calculation
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const foldThreshold = scrollTop + viewportHeight + 200; // 200px buffer

    function isAboveTheFold(element) {
      const rect = element.getBoundingClientRect();
      return rect.top + scrollTop < foldThreshold;
    }

    // Separate critical and deferred rendering
    const criticalNodes = [];
    const deferredNodes = [];

    for (const node of allNodes) {
      if (renderedNodes.has(node) || node.getAttribute("injected")) continue;
      if (isAboveTheFold(node)) {
        criticalNodes.push(node);
      } else {
        deferredNodes.push(node);
      }
    }

    // Enhanced batch rendering with frame scheduling
    function renderInBatches(nodes, batchSize = 3) {
      let index = 0;

      function processBatch() {
        const batch = nodes.slice(index, index + batchSize);
        if (batch.length === 0) return;

        batch.forEach((currentNode, nodeIndex) => {
          renderSingleComponent(currentNode, componentMap, perfLog, renderedNodes, index + nodeIndex);
        });

        index += batchSize;

        if (index < nodes.length) {
          // Use scheduler API if available for better frame timing
          if (window.scheduler?.postTask) {
            scheduler.postTask(processBatch, { priority: 'user-blocking' });
          } else if (window.requestIdleCallback) {
            requestIdleCallback(processBatch, { timeout: 16 });
          } else {
            requestAnimationFrame(processBatch);
          }
        }
      }

      processBatch();
    }

    // Render critical components immediately with smaller batches
    if (criticalNodes.length > 0) {
      renderInBatches(criticalNodes, 2); // Smaller batches for critical content
    }

    // Defer non-critical components
    if (deferredNodes.length > 0) {
      const renderDeferred = () => renderInBatches(deferredNodes, 5); // Larger batches for deferred

      if (window.requestIdleCallback) {
        requestIdleCallback(renderDeferred, { timeout: 1000 });
      } else {
        setTimeout(renderDeferred, 100);
      }
    }

    // Print performance summary after all rendering is likely complete
    setTimeout(() => perfLog.printSummary(), 500);

  } catch (e) {
    console.error(`Error injecting React Components:`, e.message);
  }
}

// Extracted component rendering logic for better maintainability
function renderSingleComponent(currentNode, componentMap, perfLog, renderedNodes, nodeIndex) {
  const componentName = currentNode.getAttribute("data-theme-id");
  const registryId = currentNode.getAttribute("data-registry-id");
  const componentDef = componentMap.get(componentName);

  if (!componentDef) return;

  const logIndex = perfLog.logStart(componentName, nodeIndex);
  const cleanup = preserveDimensions(currentNode);

  // Always reset node attributes and styles before rendering in Shopify editor or when forced
  if (isShopifyEditor() || currentNode.getAttribute("injected")) {
    currentNode.className = '';
    currentNode.removeAttribute('style');
    currentNode.removeAttribute('injected');
    currentNode.removeAttribute('id');
    currentNode.removeAttribute('data-block-props');
  }

  // Use a more efficient loading state
  currentNode.style.opacity = '0';
  currentNode.style.transition = 'opacity 0.15s ease-in-out';

  let node_data_props = null;

  // Optimized props retrieval with fallback chain
  if (registryId && window.ReactPropsRegistry.has(registryId)) {
    node_data_props = window.ReactPropsRegistry.get(registryId);
    perfLog.logProps(logIndex, performance.now());
  } else {
    // Efficient fallback prop retrieval
    node_data_props = getPropsFromDOM(currentNode);
    if (node_data_props) {
      perfLog.logProps(logIndex, performance.now());
    }
  }

  const Element = componentDef.element;
  if (!Element) {
    cleanup();
    currentNode.style.opacity = '';
    currentNode.style.transition = '';
    return;
  }

  // Get or create React root (reuse for better performance)
  let root = rootCache.get(currentNode);
  if (!root) {
    try {
      root = ReactDOM.createRoot(currentNode);
      rootCache.set(currentNode, root);
    } catch (err) {
      console.error('ReactDOM.createRoot error:', err, currentNode);
      cleanup();
      currentNode.style.opacity = '';
      currentNode.style.transition = '';
      return;
    }
  }

  const ComponentWithTransition = React.memo((props) => {
    React.useEffect(() => {
      const renderTime = performance.now();
      perfLog.logRender(logIndex, renderTime);

      // Optimize transition timing
      requestAnimationFrame(() => {
        if (currentNode) {
          currentNode.style.opacity = '1';
          perfLog.logTransition(logIndex, performance.now());

          // Cleanup after transition
          setTimeout(() => {
            currentNode.style.transition = '';
            cleanup();
            perfLog.logEnd(logIndex, performance.now());
          }, 150);
        }
      });
    }, []);

    return React.createElement(Element, props);
  });

  try {
    root.render(React.createElement(ComponentWithTransition, { settings: node_data_props }));
    currentNode.setAttribute("id", componentName + "-injected");
    currentNode.setAttribute("injected", true);
    currentNode.removeAttribute("data-block-props");
    renderedNodes.add(currentNode);
  } catch (renderError) {
    console.error(`Error rendering component ${componentName}:`, renderError, currentNode, node_data_props);
    cleanup();
    currentNode.style.opacity = '';
    currentNode.style.transition = '';
    perfLog.logEnd(logIndex, performance.now());
  }
}

// Optimized DOM-based props retrieval
function getPropsFromDOM(currentNode) {
  let propsDataJSON = null;
  let node_data_props = null;
  var injected = currentNode.getAttribute("injected");

  // Try to get props from registry if registry-id exists
  const registryId = currentNode.getAttribute("data-registry-id");
  if (registryId && window.ReactPropsRegistry && window.ReactPropsRegistry.has(registryId)) {
    return window.ReactPropsRegistry.get(registryId);
  }

  // In Shopify editor, props may be injected differently
  // if (isShopifyEditor()) {
    // Try to find a script[data-props-registry-id] in the current node or its parent
    let scriptNode = currentNode.querySelector('script[data-props-registry-id]') || currentNode.parentElement?.querySelector('script[data-props-registry-id]');
    if (scriptNode) {
      try {
        const registryData = JSON.parse(scriptNode.textContent);
        if (registryData && registryData.data) {
          return registryData.data;
        }
      } catch (error) {
        console.error('Error parsing props registry script in editor:', error, scriptNode.textContent);
      }
    }
  // }

  if (!injected) {
    let propsNode = currentNode.parentElement?.querySelector("#react-data");
    if (propsNode) {
      propsDataJSON = propsNode.getAttribute("data-injected-props");
      const decodedPropsDataJSON = decodeHTMLEntities(propsDataJSON);
      const sanitizedPropsDataJSON = sanitizeJSON(decodedPropsDataJSON);
      if (sanitizedPropsDataJSON) {
        try {
          node_data_props = JSON.parse(sanitizedPropsDataJSON);
        } catch (parseError) {
          console.log(sanitizedPropsDataJSON);
          console.error(`JSON parse error: ${parseError.message}`);
          return null;
        }
      } else {
        console.error(`Sanitization failed`);
        return null;
      }
    } else {
      if (currentNode.getAttribute("data-block-props")) {
        propsDataJSON = currentNode.getAttribute("data-block-props");
        const decodedPropsDataJSON = decodeHTMLEntities(propsDataJSON);
        const sanitizedPropsDataJSON = sanitizeJSON(decodedPropsDataJSON);
        if (sanitizedPropsDataJSON) {
          try {
            node_data_props = JSON.parse(sanitizedPropsDataJSON);
          } catch (parseError) {
            console.error(`JSON parse error: ${parseError.message}`);
            return null;
          }
        } else {
          console.error(`Sanitization failed`);
          return null;
        }
      }
    }
  }

  return node_data_props;
}

// Optimized JSON parsing with caching
const parseCache = new Map();
function decodeHTMLEntities(text) {
  const entities = {
    "&amp;": "&",
    "&quot;": '"',
    "&#39;": "'",
    "&lt;": "<",
    "&gt;": ">",
    "&nbsp;": " ",
    // Add more entities if necessary
  };
  return text.replace(/&[^\s;]+;/g, (match) => entities[match] || match);
}

function sanitizeJSON(jsonString) {
  try {
    jsonString = jsonString.replace(
      /data-sheets-(root|userformat|value)=['][^']*[']\s*/g,
      ""
    );
    let sanitizedString = jsonString
      .replace(/\\n/g, "")
      .replace(/\\t/g, "")
      .replace(/<br \/>/g, "<br/>")
      .trim();
    sanitizedString = decodeHTMLEntities(sanitizedString);
    sanitizedString = sanitizedString.replaceAll('="', "='");
    sanitizedString = sanitizedString.replaceAll('">', "'>");
    sanitizedString = sanitizedString.replaceAll('=\"', "='");
    sanitizedString = sanitizedString.replaceAll('\\"', "'");
    sanitizedString = sanitizedString.replaceAll("\\'", "'");
    sanitizedString = sanitizedString.replace(/,\s*([}\]])/g, "$1");
    return sanitizedString;
  } catch (error) {
    console.error(`Error sanitizing JSON string: ${error.message}`);
    return null;
  }
}

function parseProps(jsonString) {
  if (!jsonString) return null;
  try {
    return JSON.parse(jsonString);
  } catch (directError) {
    try {
      const sanitized = sanitizeJSON(jsonString);
      return JSON.parse(sanitized);
    } catch (sanitizedError) {
      console.error('Failed to parse props JSON:', jsonString);
      console.error('Direct error:', directError.message);
      console.error('Sanitized error:', sanitizedError.message);
      return null;
    }
  }
}
